import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useSearchParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import Login from './components/Auth/Login';
import EmailVerification from './components/Auth/EmailVerification';
import EmailVerificationModal from './components/Auth/EmailVerificationModal';
import Dashboard from './components/Dashboard/Dashboard';
import ProfileSetup from './components/Profile/ProfileSetup';
import ProviderListing from './components/Providers/ProviderListing';
import ProviderProfile from './components/Profile/ProviderProfile';
import ProviderProfileView from './components/Providers/ProviderProfileView';
import ReviewsRatings from './components/Reviews/ReviewsRatings';
import AccountSettings from './components/Account/AccountSettings';
import HelpSupport from './components/Support/HelpSupport';
import EarningsDashboard from './components/Earnings/EarningsDashboard';
import Messages from './components/Messaging/Messages';
import AdminDashboard from './components/Admin/AdminDashboard';
import UserManagement from './components/Admin/UserManagement';
import ConversationManagement from './components/Admin/ConversationManagement';
import SupportCaseManagement from './components/Admin/SupportCaseManagement';
import SessionManagement from './components/Admin/SessionManagement';
import Layout from './components/Layout/Layout';
import ProviderSessions from './components/Sessions/ProviderSessions';
import PractitionerSessions from './components/Sessions/PractitionerSessions';
import SessionDetailView from './components/Sessions/SessionDetailView';
import Terms from './components/Legal/Terms';
import Privacy from './components/Legal/Privacy';
import ProviderGuidelines from './components/ProviderGuidelines';
import PractitionerProfile from './components/Profile/PractitionerProfile';
import PractitionerListing from './components/Practitioners/PractitionerListing';
import './App.css';

// Component to handle email verification parameters
function EmailVerificationHandler({ showModal, setShowModal }) {
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');
    
    if (mode === 'verifyEmail' && oobCode) {
      setShowModal(true);
      
      // Store that we're handling verification to prevent other components from interfering
      window.handlingEmailVerification = true;
    }
  }, [searchParams, setShowModal]);

  const handleClose = () => {
    window.handlingEmailVerification = false;
    setShowModal(false);
  };

  return showModal ? (
    <EmailVerificationModal 
      onClose={handleClose}
    />
  ) : null;
}

function AppContent() {
  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
    
  }, [currentUser]);

  const fetchUserProfile = async () => {
    try {
      const docRef = doc(db, 'users', currentUser.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setUserProfile(docSnap.data());
      } else {
        setUserProfile({ isProfileComplete: false });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUserProfile({ isProfileComplete: false });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }
  
  return (
    <Router>
      <div className="App">
        <EmailVerificationHandler 
          showModal={showVerificationModal}
          setShowModal={setShowVerificationModal}
        />
        <Layout>
          <Routes>
            {/* Public routes - accessible to everyone */}
            <Route path="/" element={currentUser ? (userProfile?.isProfileComplete ? <Dashboard /> : <ProfileSetup />) : <Dashboard />} />
            <Route path="/providers" element={<ProviderListing />} />
            <Route path="/provider/:username" element={<ProviderProfileView />} />
            
            {/* Legal Pages - accessible to everyone */}
            <Route path="/provider-guidelines" element={<ProviderGuidelines />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            
            
            {/* Protected routes - redirect to login if not authenticated */}
            <Route path="/practitioner-profile" element={currentUser ? <PractitionerProfile /> : <Login />} />
            <Route path="/practitioner-sessions" element={currentUser ? <PractitionerSessions /> : <Login />} />
            <Route path="/session/:sessionId" element={currentUser ? <SessionDetailView /> : <Login />} />
            <Route path="/provider-profile" element={currentUser ? <ProviderProfile /> : <Login />} />
            <Route path="/provider-sessions" element={currentUser ? <ProviderSessions /> : <Login />} />
            <Route path="/connect-practitioners" element={currentUser ? <PractitionerListing /> : <Login />} />
            <Route path="/reviews-ratings" element={currentUser ? <ReviewsRatings /> : <Login />} />
            <Route path="/earnings" element={currentUser ? <EarningsDashboard /> : <Login />} />
            <Route path="/messages" element={currentUser ? <Messages /> : <Login />} />
            <Route path="/account" element={currentUser ? <AccountSettings /> : <Login />} />
            <Route path="/help-support" element={currentUser ? <HelpSupport /> : <Login />} />
            
            {/* Admin routes */}
            <Route path="/admin/dashboard" element={currentUser ? <AdminDashboard /> : <Login />} />
            <Route path="/admin/users" element={currentUser ? <UserManagement /> : <Login />} />
            <Route path="/admin/conversations" element={currentUser ? <ConversationManagement /> : <Login />} />
            <Route path="/admin/support-cases" element={currentUser ? <SupportCaseManagement /> : <Login />} />
            <Route path="/admin/sessions" element={currentUser ? <SessionManagement /> : <Login />} />
            
            {/* Login and verification routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<EmailVerification />} />
          </Routes>
        </Layout>
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
